import { ActionIcon, AppShell, Burger, Group, Image, Text, UnstyledButton } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { NotificationsPopover } from '@/components/NotificationsPopover/NotificationsPopover';
import { TextTruncate } from '@/components/TextTruncate/TextTruncate';
import { VisibleBetween } from '@/components/VisibleBetween/VisibleBetween';
import { useAuth, useGlobalSettings, useImpersonation, useScreenSize } from '@/hooks';
import { impersonationSessionService } from '@/services/impersonationSession.service';
import { IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand } from '@tabler/icons-react';
import classNames from 'classnames';
import { getWebPartition } from '@/helpers';
import { WEB_PARTITION } from '@/types';
import logoMPA from '@/assets/logo-mpa.png';
import logoMessages from '@/assets/messages-logo.svg';
import logoForms from '@/assets/logo-forms.svg';
import logoMinistries from '@/assets/logo-ministries.svg';
import styles from './MainHeader.module.sass';

const PARTITION_LOGO: Record<WEB_PARTITION, string> = {
  [WEB_PARTITION.MPA]: logoMPA,
  [WEB_PARTITION.MESSAGES]: logoMessages,
  [WEB_PARTITION.FORMS]: logoForms,
  [WEB_PARTITION.MINISTRIES]: logoMinistries,
};

type MainHeaderProps = {
  isMobileMenuOpened: boolean;
  toggleMobileMenu: () => void;
  isDesktopMenuExpanded: boolean;
  toggleDesktopMenuExpanded: () => void;
};

export const MainHeader = ({
  isMobileMenuOpened,
  toggleMobileMenu,
  isDesktopMenuExpanded,
  toggleDesktopMenuExpanded,
}: MainHeaderProps) => {
  const { userInfo } = useAuth();
  const { currentChurch } = useGlobalSettings();
  const { impersonatedUserEmail, impersonatedUserName, isImpersonating } = useImpersonation();
  const location = useLocation();
  const currentPartition = getWebPartition(location.pathname);
  const mobileLogo = PARTITION_LOGO[currentPartition];

  const { isDesktop } = useScreenSize();
  const isImpersonationVisible = Boolean(isImpersonating && impersonatedUserEmail);
  const impersonatedDisplayName = impersonatedUserName || impersonatedUserEmail || '';
  const superadminName =
    [userInfo?.profile?.given_name, userInfo?.profile?.family_name].filter(Boolean).join(' ').trim() ||
    userInfo?.profile?.preferred_username ||
    userInfo?.profile?.email ||
    '';
  const churchDisplayName = currentChurch?.churchName || '';

  const handleStopImpersonation = (): void => {
    void impersonationSessionService.endSession();
  };

  return (
    <AppShell.Header
      className={classNames(styles.inner, {
        [styles.impersonationMode]: isImpersonationVisible,
      })}
    >
      <Group px="sm" wrap="nowrap">
        {isDesktop && (
          <ActionIcon
            onClick={toggleDesktopMenuExpanded}
            size="md"
            radius="100%"
            variant="light"
            bg="transparent"
            color="base.6"
          >
            {isDesktopMenuExpanded ? (
              <IconLayoutSidebarLeftCollapse size={20} stroke={1.5} />
            ) : (
              <IconLayoutSidebarLeftExpand size={20} stroke={1.5} />
            )}
          </ActionIcon>
        )}

        <VisibleBetween to="lg">
          {!isMobileMenuOpened ? <Burger opened={isMobileMenuOpened} onClick={toggleMobileMenu} size="sm" /> : null}
        </VisibleBetween>

        {isDesktop ? (
          <TextTruncate text={churchDisplayName} className={styles.churchName} />
        ) : (
          <Image src={mobileLogo} className={styles.logo} alt="myParish logo" />
        )}
      </Group>

      {isImpersonationVisible ? (
        <Group gap="lg" wrap="nowrap" className={styles.impersonationControls}>
          <Text fw={500} className={styles.impersonationText}>
            <b>{superadminName}</b> impersonating <b>{impersonatedDisplayName}</b>
          </Text>
          <UnstyledButton onClick={handleStopImpersonation} className={styles.stopImpersonationButton}>
            End impersonation
          </UnstyledButton>
        </Group>
      ) : null}

      <Group px="md" gap={8} wrap="nowrap">
        <NotificationsPopover />
      </Group>
    </AppShell.Header>
  );
};
